import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Check, Copy, RotateCcw, Sparkles, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { OutOfTokensAlert } from '@/components/billing/OutOfTokens'
import {
    acceptAIGeneration,
    aiErrorMessage,
    enhanceListingCopy,
    formatTokens,
    generateListingCopy,
    getAIOverview,
    isInsufficientTokens,
    type AIEnhanceAction,
    type AIOverview,
    type EnhanceActionKey,
    type ListingCopy,
    type ListingDraft,
    type ListingLanguage,
    type ListingTone,
} from '@/lib/ai'

/**
 * "Generate with AI" and "Improve with AI" for the listing form.
 *
 * The agent always sees the draft before anything reaches the form, and
 * accepting only fills the fields — the listing still goes through the normal
 * submit and approval flow. Facts the agent did not supply are listed rather
 * than invented, which is the whole point of showing `missingFacts`.
 */
const TONES: { value: ListingTone; label: string }[] = [
    { value: 'professional', label: 'Professional' },
    { value: 'warm', label: 'Warm' },
    { value: 'luxury', label: 'Luxury' },
    { value: 'concise', label: 'Concise' },
    { value: 'persuasive', label: 'Persuasive' },
]

const LANGUAGES: { value: ListingLanguage; label: string }[] = [
    { value: 'en', label: 'English' },
    { value: 'ms', label: 'Bahasa Malaysia' },
    { value: 'zh', label: 'Chinese' },
]

/** Actions whose result replaces the description, the headline, or neither. */
const APPLIES_TO_DESCRIPTION: EnhanceActionKey[] = ['rewrite', 'professional', 'shorter', 'persuasive', 'translate']
const APPLIES_TO_HEADLINE: EnhanceActionKey[] = ['headline']

const wordCount = (value: string) => value.trim().split(/\s+/).filter(Boolean).length

const splitList = (value: string) =>
    value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)

interface AIListingAssistantProps {
    mode: 'generate' | 'enhance'
    listingId?: number
    getDraft?: () => ListingDraft
    currentDescription?: string
    onAccept: (values: { headline?: string; description?: string }) => void
}

const AIListingAssistant = ({ mode, listingId, getDraft, currentDescription, onAccept }: AIListingAssistantProps) => {
    const [open, setOpen] = useState(false)
    const [overview, setOverview] = useState<AIOverview | null>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<{ message: string; insufficient: boolean } | null>(null)

    // Generate
    const [notes, setNotes] = useState('')
    const [sellingPoints, setSellingPoints] = useState('')
    const [landmarks, setLandmarks] = useState('')
    const [tone, setTone] = useState<ListingTone>('professional')
    const [language, setLanguage] = useState<ListingLanguage>('en')
    const [copy, setCopy] = useState<ListingCopy | null>(null)
    const [missingFacts, setMissingFacts] = useState<string[]>([])

    // Enhance
    const [action, setAction] = useState<EnhanceActionKey | null>(null)
    const [translateTo, setTranslateTo] = useState<ListingLanguage>('ms')
    const [result, setResult] = useState<string>('')
    const [resultNotes, setResultNotes] = useState<string[]>([])

    const [generationId, setGenerationId] = useState<number | null>(null)
    const [balanceAfter, setBalanceAfter] = useState<number | null>(null)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        if (!open || overview) return
        getAIOverview()
            .then(setOverview)
            .catch(() => setOverview(null))
    }, [open, overview])

    const operationCost = useMemo(() => {
        if (!overview) return null
        const key = mode === 'generate' ? 'listing_generation' : action ? enhanceOperation(overview.enhanceActions, action) : null
        if (!key) return null
        return overview.operations.find((op) => op.operationKey === key)?.tokenCost ?? null
    }, [overview, mode, action])

    const available = overview?.balance?.availableTokens ?? null
    const notConfigured = overview?.status && !overview.status.openai.configured

    const reset = () => {
        setCopy(null)
        setResult('')
        setResultNotes([])
        setError(null)
        setGenerationId(null)
        setBalanceAfter(null)
        setCopied(false)
    }

    const runGenerate = async () => {
        if (!getDraft) return
        setBusy(true)
        setError(null)
        try {
            const data = await generateListingCopy({
                ...(listingId ? { listingId } : {}),
                draft: getDraft(),
                notes: {
                    notes,
                    sellingPoints: splitList(sellingPoints),
                    nearbyLandmarks: splitList(landmarks),
                    tone,
                    language,
                },
            })
            setCopy(data.copy)
            setMissingFacts(data.missingFacts || [])
            setGenerationId(data.generationId)
            setBalanceAfter(data.usage?.balanceAfter ?? null)
        } catch (err) {
            setError({ message: aiErrorMessage(err), insufficient: isInsufficientTokens(err) })
        } finally {
            setBusy(false)
        }
    }

    const runEnhance = async (key: EnhanceActionKey) => {
        if (!listingId) return
        setAction(key)
        setBusy(true)
        setError(null)
        setResult('')
        try {
            const data = await enhanceListingCopy(listingId, {
                action: key,
                ...(currentDescription ? { text: currentDescription } : {}),
                ...(key === 'translate' ? { language: translateTo } : {}),
            })
            setResult(data.result)
            setResultNotes(data.notes || [])
            setGenerationId(data.generationId)
            setBalanceAfter(data.usage?.balanceAfter ?? null)
        } catch (err) {
            setError({ message: aiErrorMessage(err), insufficient: isInsufficientTokens(err) })
        } finally {
            setBusy(false)
        }
    }

    const confirmAccepted = async () => {
        if (!generationId) return
        try {
            await acceptAIGeneration(generationId)
        } catch {
            // Non-critical: the copy is already in the form.
        }
    }

    const acceptGenerated = async () => {
        if (!copy) return
        onAccept({ headline: copy.title, description: copy.fullDescription })
        await confirmAccepted()
        toast.success('Draft loaded into the form. Check the facts before submitting.')
        setOpen(false)
        reset()
    }

    const acceptEnhanced = async () => {
        if (!result || !action) return
        if (APPLIES_TO_HEADLINE.includes(action)) onAccept({ headline: result })
        else onAccept({ description: result })
        await confirmAccepted()
        toast.success('Applied to the form. Review it before saving.')
        setOpen(false)
        reset()
    }

    const copyResult = async () => {
        try {
            await navigator.clipboard.writeText(result)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            toast.error('Could not copy to the clipboard.')
        }
    }

    const isApplyable = action ? APPLIES_TO_DESCRIPTION.includes(action) || APPLIES_TO_HEADLINE.includes(action) : false

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                setOpen(next)
                if (!next) reset()
            }}>
            <DialogTrigger asChild>
                <Button type='button' variant='outline' size='sm'>
                    {mode === 'generate' ? <Sparkles className='h-3.5 w-3.5' /> : <Wand2 className='h-3.5 w-3.5' />}
                    {mode === 'generate' ? 'Generate with AI' : 'Improve with AI'}
                </Button>
            </DialogTrigger>
            <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto'>
                <DialogHeader>
                    <DialogTitle>{mode === 'generate' ? 'Generate listing copy' : 'Improve this listing'}</DialogTitle>
                    <DialogDescription>
                        {mode === 'generate'
                            ? 'The copy is written from the details you entered. Anything you have not filled in is left out rather than guessed.'
                            : 'Rewrites keep every fact already in the listing. Review the result before applying it.'}
                    </DialogDescription>
                </DialogHeader>

                {notConfigured && (
                    <Alert className='border-amber-300 bg-amber-50'>
                        <AlertDescription>
                            {overview?.status.openai.message || 'AI is not configured on this environment yet.'}
                        </AlertDescription>
                    </Alert>
                )}

                {(operationCost !== null || available !== null) && (
                    <p className='text-xs text-muted-foreground'>
                        {operationCost !== null && <>Costs {formatTokens(operationCost)} tokens. </>}
                        {available !== null && <>You have {formatTokens(balanceAfter ?? available)} tokens.</>}
                    </p>
                )}

                {error &&
                    (error.insufficient ? (
                        <OutOfTokensAlert message={error.message} />
                    ) : (
                        <Alert className='border-red-300 bg-red-50'>
                            <AlertDescription>{error.message}</AlertDescription>
                        </Alert>
                    ))}

                {mode === 'generate' && !copy && (
                    <div className='space-y-3'>
                        <div>
                            <Label className='text-xs'>Notes for the writer (optional)</Label>
                            <Textarea
                                className='mt-1'
                                rows={3}
                                value={notes}
                                placeholder='Anything the details do not capture: renovation, view, why the owner is selling.'
                                onChange={(e) => setNotes(e.target.value)} />
                        </div>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                            <div>
                                <Label className='text-xs'>Selling points (comma separated)</Label>
                                <Input className='mt-1' value={sellingPoints} placeholder='Main road frontage, corner lot' onChange={(e) => setSellingPoints(e.target.value)} />
                            </div>
                            <div>
                                <Label className='text-xs'>Nearby landmarks (comma separated)</Label>
                                <Input className='mt-1' value={landmarks} placeholder='Sunway Pyramid, SMK Taman SEA' onChange={(e) => setLandmarks(e.target.value)} />
                            </div>
                            <div>
                                <Label className='text-xs'>Tone</Label>
                                <Select value={tone} onValueChange={(v) => setTone(v as ListingTone)}>
                                    <SelectTrigger className='mt-1 w-full'><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className='text-xs'>Language</Label>
                                <Select value={language} onValueChange={(v) => setLanguage(v as ListingLanguage)}>
                                    <SelectTrigger className='mt-1 w-full'><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                )}

                {mode === 'enhance' && !result && (
                    <div className='space-y-3'>
                        <div className='flex flex-wrap gap-2'>
                            {(overview?.enhanceActions || []).map((item) => (
                                <Button
                                    key={item.key}
                                    type='button'
                                    variant={action === item.key ? 'default' : 'outline'}
                                    size='sm'
                                    disabled={busy}

                                    onClick={() => runEnhance(item.key)}>
                                    {item.label}
                                </Button>
                            ))}
                        </div>
                        <div>
                            <Label className='text-xs'>Translate into</Label>
                            <Select value={translateTo} onValueChange={(v) => setTranslateTo(v as ListingLanguage)}>
                                <SelectTrigger className='mt-1 w-full sm:w-60'><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <p className='text-xs text-muted-foreground mt-1'>Used only by the Translate action.</p>
                        </div>
                    </div>
                )}

                {busy && (
                    <div className='flex items-center gap-2 py-6 justify-center'>
                        <Spinner variant='circle' className='h-4 w-4' />
                        <span className='text-sm text-muted-foreground'>Writing…</span>
                    </div>
                )}

                {copy && !busy && (
                    <div className='space-y-4'>
                        <div>
                            <Label className='text-xs uppercase text-muted-foreground'>Headline</Label>
                            <p className='font-semibold'>{copy.title}</p>
                        </div>
                        <div>
                            <Label className='text-xs uppercase text-muted-foreground'>Description ({wordCount(copy.fullDescription)} words)</Label>
                            <p className='text-sm whitespace-pre-wrap'>{copy.fullDescription}</p>
                        </div>
                        {copy.shortDescription && (
                            <div>
                                <Label className='text-xs uppercase text-muted-foreground'>Short description</Label>
                                <p className='text-sm'>{copy.shortDescription}</p>
                            </div>
                        )}
                        {copy.highlights?.length > 0 && (
                            <div>
                                <Label className='text-xs uppercase text-muted-foreground'>Highlights</Label>
                                <ul className='text-sm list-disc ml-5'>{copy.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>
                            </div>
                        )}
                        {copy.sellingPoints?.length > 0 && (
                            <div>
                                <Label className='text-xs uppercase text-muted-foreground'>Selling points</Label>
                                <ul className='text-sm list-disc ml-5'>{copy.sellingPoints.map((h, i) => <li key={i}>{h}</li>)}</ul>
                            </div>
                        )}
                        {copy.locationDescription && (
                            <div>
                                <Label className='text-xs uppercase text-muted-foreground'>Location</Label>
                                <p className='text-sm'>{copy.locationDescription}</p>
                            </div>
                        )}
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                            <div>
                                <Label className='text-xs uppercase text-muted-foreground'>SEO title</Label>
                                <p className='text-sm'>{copy.seoTitle}</p>
                            </div>
                            <div>
                                <Label className='text-xs uppercase text-muted-foreground'>SEO description</Label>
                                <p className='text-sm'>{copy.seoDescription}</p>
                            </div>
                        </div>
                        {copy.tags?.length > 0 && (
                            <div className='flex flex-wrap gap-1'>
                                {copy.tags.map((t) => <Badge key={t} variant='secondary'>{t}</Badge>)}
                            </div>
                        )}
                        {copy.socialCopy && (
                            <div>
                                <Label className='text-xs uppercase text-muted-foreground'>Social copy</Label>
                                <p className='text-sm'>{copy.socialCopy}</p>
                            </div>
                        )}
                        {missingFacts.length > 0 && (
                            <Alert className='border-amber-300 bg-amber-50'>
                                <AlertDescription className='text-xs'>
                                    <span className='font-medium'>Not provided, so not included:</span> {missingFacts.join(', ')}
                                </AlertDescription>
                            </Alert>
                        )}
                        {copy.disclaimer && <p className='text-xs text-muted-foreground'>{copy.disclaimer}</p>}
                    </div>
                )}

                {result && !busy && (
                    <div className='space-y-3'>
                        <div>
                            <Label className='text-xs uppercase text-muted-foreground'>Result</Label>
                            <p className='text-sm whitespace-pre-wrap'>{result}</p>
                        </div>
                        {resultNotes.length > 0 && (
                            <ul className='text-xs text-muted-foreground list-disc ml-5'>{resultNotes.map((n, i) => <li key={i}>{n}</li>)}</ul>
                        )}
                    </div>
                )}

                <DialogFooter className='gap-2 sm:gap-2'>
                    {mode === 'generate' && !copy && (
                        <Button type='button' disabled={busy || Boolean(notConfigured)} onClick={runGenerate}>
                            <Sparkles className='h-4 w-4' /> Generate
                        </Button>
                    )}
                    {copy && (
                        <>
                            <Button type='button' variant='outline' disabled={busy} onClick={runGenerate}>
                                <RotateCcw className='h-4 w-4' /> Regenerate
                            </Button>
                            <Button type='button' onClick={acceptGenerated}>
                                <Check className='h-4 w-4' /> Accept
                            </Button>
                        </>
                    )}
                    {result && action && (
                        <>
                            <Button type='button' variant='outline' disabled={busy} onClick={() => runEnhance(action)}>
                                <RotateCcw className='h-4 w-4' /> Regenerate
                            </Button>
                            {isApplyable ? (
                                <Button type='button' onClick={acceptEnhanced}>
                                    <Check className='h-4 w-4' /> Apply
                                </Button>
                            ) : (
                                <Button type='button' onClick={copyResult}>
                                    <Copy className='h-4 w-4' /> {copied ? 'Copied' : 'Copy'}
                                </Button>
                            )}
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/** The operation an enhancement action is billed under, for the cost line. */
function enhanceOperation(actions: AIEnhanceAction[], key: EnhanceActionKey): string | null {
    return actions.find((a) => a.key === key)?.operationKey ?? null
}

export default AIListingAssistant
